import ReactDOM from 'react-dom'

import { isTablet } from '../../utils/mediaQueries'
import LanguageSwitcher from '../Header/LanguageSwitcher/LanguageSwitcher'
import Search from '../Header/Search/Search'
import ProductWishlistWidget from '../Wishlist/ProductWishlistWidget/ProductWishlistWidget'
import { getWishlist } from '../Wishlist/WishlistStorage'

const COMPONENTS_MAP = {
  wishlist: ProductWishlistWidget,
  search: Search,
  languageSwitcher: LanguageSwitcher,
}

// Modal content class names
const MODAL_CONTENT_CLASSES = {
  hamburger: 'modal-content-hamburger',
  search: 'modal-content-search',
  languageSwitcher: 'modal-content-language',
  wishlist: 'modal-content-wishlist',
}

// CSS selectors
const HEADER_SELECTOR = '.cmp-header-v2'
const HEADER_IS_FIXED_CLASS = 'cmp-header-v2--is-fixed'
const HEADER_IS_OPENED_CLASS = 'cmp-header-v2--is-opened'
const HEADER_IS_HIDDEN_CLASS = 'cmp-header-v2--is-hidden'
const HEADER_IS_SCROLLED_CLASS = 'cmp-header-v2--is-scrolled'
const MODAL_BODY_SELECTOR = '#cmp-header-v2__modal-body'
const MODAL_BODY_CLASS = 'cmp-header-v2__modal-body'
const MODAL_CONTENT_SELECTOR = '.cmp-header-v2__modal-content'
const MODAL_SELECTOR = '#cmp-header-v2__modal'
const MODAL_OVERLAY_SELECTOR = 'cmp-header-v2__modal-overlay'
const HEADER_MENU_ITEM_IS_HIDDEN_CLASS = 'cmp-header-v2__menu-item--is-hidden'
const MODAL_CLOSE_BUTTON_SELECTOR = '.cmp-header-v2__modal-close'
const MODAL_IS_VISIBLE_CLASS = 'cmp-header-v2__modal--is-visible'
const COUNTRY_LANGUAGE_SWITCHER_SELECTOR = '.cmp-header-v2__menu-item-country-language-switcher'
const DIVISION_SELECTOR = '.cmp-header-v2__division'
const DROPDOWN_SELECTOR = '.cmp-header-v2__dropdown'
const DROPDOWN_BUTTON_SELECTOR = '.cmp-header-v2__dropdown-button'
const DROPDOWN_OPENED_CLASS = 'cmp-header-v2__dropdown--is-opened'
const NAVIGATION_MENU_ITEM_CLASS = 'cmp-navigation-v2__menu-item'
const NAVIGATION_ITEM_EXPANDED_CLASS = 'cmp-navigation-v2__menu-item--is-expanded'
const HAMBURGER_TOGGLE_OPENED_CLASS = 'cmp-header-v2__hamburger-toggle--is-opened'
const NAVIGATION_L2_VISIBLE_CLASS = 'cmp-navigation-v2__level2--is-visible'
const MODAL_NAVIGATION_CLASS = 'cmp-header-v2__modal--is-navigation'
const ARIA_EXPANDED = 'aria-expanded'
const ARIA_LABEL = 'aria-label'

// Animation timing constants
const DEBOUNCE_DEFAULT_WAIT = 10

// eslint-disable-next-line sonarjs/cognitive-complexity
;(function () {
  'use strict'

  const headerElement = document.querySelector(HEADER_SELECTOR)
  if (!headerElement) return

  // DOM caching
  const DOM_CACHE = new Map()

  // Elements access with caching
  const elements = {
    getElement(selector) {
      if (!DOM_CACHE.has(selector)) {
        DOM_CACHE.set(selector, document.querySelector(selector))
      }
      return DOM_CACHE.get(selector)
    },
    getHeader() {
      return this.getElement(HEADER_SELECTOR)
    },
    getModal() {
      return this.getElement(MODAL_SELECTOR)
    },
    getModalBody() {
      return this.getElement(MODAL_BODY_SELECTOR)
    },
    getModalContent() {
      return this.getElement(MODAL_CONTENT_SELECTOR)
    },
  }

  // Component state
  let isHeaderOpened = false
  let headerDropdownContent = null
  let lastScrollY = window.scrollY
  let currentTriggerButton = null
  let modalKeydownListenerKey = null
  const allDropdownTriggers = []

  /**
   * Utility function to debounce frequent events
   */
  function debounce(functionToDebounce, wait = DEBOUNCE_DEFAULT_WAIT) {
    let timeout
    return function executedFunction(...functionArguments) {
      const later = () => {
        clearTimeout(timeout)
        functionToDebounce(...functionArguments)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  /**
   * Safely query DOM element
   */
  function safeQuerySelector(parent, selector) {
    if (!parent) return null
    try {
      return parent.querySelector(selector)
    } catch (error) {
      console.error(`Error querying for ${selector}:`, error)
      return null
    }
  }

  /**
   * Event management utility
   */
  const eventManager = {
    listeners: new Map(),

    add(element, eventType, handler, options) {
      if (!element) return false

      const key = `${element.tagName || 'unknown'}-${eventType}-${Date.now()}`
      this.listeners.set(key, { element, eventType, handler })
      element.addEventListener(eventType, handler, options)
      return key
    },

    remove(key) {
      if (!this.listeners.has(key)) return false

      const { element, eventType, handler } = this.listeners.get(key)
      element.removeEventListener(eventType, handler)
      this.listeners.delete(key)
      return true
    },

    removeAll() {
      for (const [_key, { element, eventType, handler }] of this.listeners.entries()) {
        element?.removeEventListener?.(eventType, handler)
      }
      this.listeners.clear()
    },
  }

  /**
   * Component mounting and cleanup management
   */
  const componentManager = {
    mounts: new Map(),

    mountComponent(contentId, rootElement) {
      if (!COMPONENTS_MAP[contentId] || !rootElement) return false

      try {
        const dataJson = JSON.parse(rootElement.dataset.cmpDataJson || '{}')
        const Component = COMPONENTS_MAP[contentId]

        // Use render instead of createRoot
        ReactDOM.render(<Component config={dataJson} />, rootElement)

        const mountKey = `${contentId}-${Date.now()}`
        this.mounts.set(mountKey, { rootElement })
        return mountKey
      } catch (error) {
        console.error('Failed to mount component', contentId, error)
        return false
      }
    },

    unmountComponent(mountKey) {
      if (!this.mounts.has(mountKey)) return false

      try {
        const { rootElement } = this.mounts.get(mountKey)
        ReactDOM.unmountComponentAtNode(rootElement)
        this.mounts.delete(mountKey)
        return true
      } catch (error) {
        console.error('Failed to unmount component', error)
        return false
      }
    },

    unmountAll() {
      for (const [key, _] of this.mounts.entries()) this.unmountComponent(key)
    },
  }

  /**
   * Handle modal trigger click events
   */
  function modalTriggerClickHandler(event_, button = null) {
    const buttonElement = button || event_.currentTarget
    const contentId = buttonElement.dataset.modalTrigger

    if (contentId === 'close') {
      closeModal()
      event_.preventDefault()
      event_.stopPropagation()
      return
    }

    if (buttonElement.closest('.cmp-header-v2__hamburger') !== null) {
      if (buttonElement.classList.contains(HAMBURGER_TOGGLE_OPENED_CLASS)) {
        closeModal()
      } else {
        openModal(contentId)
      }
    } else {
      handleDropdownClick(contentId, event_, buttonElement)
    }

    event_.stopPropagation()
  }

  /**
   * Handle keyboard navigation - Enter/Space opens modal and focuses first link
   */
  function handleMenuItemKeydown(event_, button) {
    if (event_.key === 'Enter' || event_.key === ' ') {
      event_.preventDefault()
      modalTriggerClickHandler(event_, button)
    }
  }

  /**
   * Handle focus trap - keep Tab cycling between trigger button and modal content
   */
  function handleModalKeydown(event_) {
    if (event_.key === 'Escape') {
      event_.preventDefault()
      closeModal()
      return
    }

    if (event_.key === 'Tab' && currentTriggerButton) {
      const modal = elements.getModal()
      if (!modal) return

      const closeButton = modal.querySelector('.cmp-header-v2__modal-close')
      const logoLink = modal.querySelector('.cmp-header-v2__modal-body .cmp-logo a')
      const contentFocusable = modal.querySelectorAll(
        '.cmp-header-v2__modal-body a[href]:not(.cmp-logo a), .cmp-header-v2__modal-body button:not([disabled]), .cmp-header-v2__modal-body input:not([disabled])',
      )

      const focusableElements = [closeButton, ...contentFocusable, logoLink].filter(Boolean)

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event_.shiftKey) {
        if (document.activeElement === firstElement) {
          event_.preventDefault()
          currentTriggerButton.focus()
        } else if (document.activeElement === currentTriggerButton) {
          event_.preventDefault()
          lastElement.focus()
        }
      } else {
        if (document.activeElement === currentTriggerButton) {
          event_.preventDefault()
          firstElement.focus()
        } else if (document.activeElement === lastElement) {
          event_.preventDefault()
          currentTriggerButton.focus()
        }
      }
    }
  }

  /**
   * Close all open dropdowns
   */
  function closeAllDropdowns() {
    const openDropdowns = document.querySelectorAll(`.${DROPDOWN_OPENED_CLASS}`)
    for (const dropdown of openDropdowns) {
      dropdown.classList.remove(DROPDOWN_OPENED_CLASS)
      const button = dropdown.querySelector(DROPDOWN_BUTTON_SELECTOR)
      if (button) {
        button.setAttribute(ARIA_EXPANDED, 'false')
      }

      if (dropdown.dataset.keydownHandlerKey) {
        eventManager.remove(dropdown.dataset.keydownHandlerKey)
        delete dropdown.dataset.keydownHandlerKey
      }
    }
  }

  /**
   * Handle dropdown keyboard navigation (Tab cycling only)
   */
  function handleDropdownKeydown(event_, trigger, button) {
    if (event_.key !== 'Tab') return

    const dropdown = safeQuerySelector(trigger, DROPDOWN_SELECTOR)
    if (!dropdown) return

    const focusableElements = dropdown.querySelectorAll('a[href], button:not([disabled])')
    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event_.shiftKey) {
      if (document.activeElement === firstElement) {
        event_.preventDefault()
        button.focus()
      } else if (document.activeElement === button) {
        event_.preventDefault()
        lastElement.focus()
      }
    } else {
      if (document.activeElement === button) {
        event_.preventDefault()
        firstElement.focus()
      } else if (document.activeElement === lastElement) {
        event_.preventDefault()
        button.focus()
      }
    }
  }

  /**
   * Handle dropdown trigger click events
   */
  function dropdownTriggerClickHandler(event_) {
    const trigger = event_.currentTarget
    const dropdown = safeQuerySelector(trigger, DROPDOWN_SELECTOR)
    if (!dropdown || dropdown.contains(event_.target)) {
      event_.stopPropagation()
      return
    }

    if (isHeaderOpened && headerDropdownContent !== null) {
      closeModal()
    }

    const isAlreadyOpen = trigger.classList.contains(DROPDOWN_OPENED_CLASS)
    closeAllDropdowns()

    if (!isAlreadyOpen) {
      trigger.classList.add(DROPDOWN_OPENED_CLASS)
      const button = trigger.querySelector(DROPDOWN_BUTTON_SELECTOR)
      if (button) {
        button.setAttribute(ARIA_EXPANDED, 'true')

        const keydownHandler = (event_) => handleDropdownKeydown(event_, trigger, button)
        const listenerKey = eventManager.add(document, 'keydown', keydownHandler)

        trigger.dataset.keydownHandlerKey = listenerKey
      }
    }

    event_.stopPropagation()
  }

  function toggleClass({ element, className = 'hidden', isVisible = true }) {
    element.classList.toggle(className, !isVisible)
  }

  function toggleItemsVisibility(items, isVisible) {
    for (const item of items) {
      toggleClass({ element: item, isVisible })
    }
  }

  function toggleDropdownVisibility(dropdownElement, isVisible) {
    if (!dropdownElement) {
      return
    }

    toggleClass({ element: dropdownElement, isVisible })
  }

  function toggleLevel2Visibility(level2Element, isVisible = true) {
    if (!level2Element) {
      return
    }

    toggleClass({ element: level2Element, className: NAVIGATION_L2_VISIBLE_CLASS, isVisible: !isVisible })
    const parent = level2Element.parentElement

    if (parent) {
      toggleClass({ element: parent, isVisible })
    }
  }

  /**
   * Handle level 1 navigation item clicks
   */
  function level1ItemClickHandler(event_) {
    const level1Item = event_.currentTarget
    event_.stopPropagation()

    const modalBody = elements.getModalBody()

    if (!modalBody) {
      return
    }

    const isDivision = level1Item.classList.contains('cmp-header-v2__division-title')
    const level1Items = modalBody.querySelectorAll('.cmp-navigation-v2__mobile-level1, .cmp-header-v2__division-title')
    const level2Items = modalBody.querySelectorAll('.cmp-navigation-v2__mobile-level2')

    // Hide level 1 items
    toggleItemsVisibility(level1Items, false)
    // Hide level 2 items
    toggleItemsVisibility(level2Items, false)

    let targetLevel2 = null

    if (level1Item.classList.contains('cmp-header-v2__division-title')) {
      const parent = level1Item.closest('.cmp-header-v2__division-wrapper')
      targetLevel2 = safeQuerySelector(parent, '.cmp-header-v2__division-dropdown')
    } else {
      targetLevel2 = level1Item.nextElementSibling
    }

    if (!targetLevel2) {
      return
    }

    if (isDivision) {
      toggleDropdownVisibility(targetLevel2, true)
    } else {
      const level2Content = safeQuerySelector(targetLevel2, '.cmp-navigation-v2__level2')
      toggleLevel2Visibility(level2Content, true)
    }

    const backButton = safeQuerySelector(
      targetLevel2,
      '.cmp-navigation-v2__level2-go-back, .cmp-header-v2__division-back',
    )

    if (!backButton) {
      return
    }

    eventManager.add(backButton, 'click', () => {
      if (isDivision) {
        toggleDropdownVisibility(targetLevel2, false)
      } else {
        const level2Content = safeQuerySelector(targetLevel2, '.cmp-navigation-v2__level2')
        toggleLevel2Visibility(level2Content, false)
      }

      // Show level 1 items
      toggleItemsVisibility(level1Items, true)
    })
  }

  /**
   * Attach modal trigger listeners to elements
   */
  function attachModalTriggerListeners(parentElement = document) {
    const modalTriggers = parentElement.querySelectorAll('[data-modal-trigger]')
    for (const button of modalTriggers) {
      if (button.dataset.modalTrigger === 'search' || button.dataset.modalTrigger === 'wishlist') {
        eventManager.add(button.parentElement, 'click', (event) => modalTriggerClickHandler(event, button))
        eventManager.add(button.parentElement, 'keydown', (event) => handleMenuItemKeydown(event, button))
      } else {
        eventManager.add(button, 'click', (event) => modalTriggerClickHandler(event))
        eventManager.add(button, 'keydown', (event) => handleMenuItemKeydown(event))
      }
    }
  }

  /**
   * Toggle hamburger button state between hamburger and close (X) icon
   * @param {boolean} isOpen - Whether to show the close (X) icon (true) or hamburger icon (false)
   */
  function toggleHamburgerIcon(isOpen) {
    const hamburgerToggle = document.querySelector('.cmp-header-v2__hamburger-toggle')
    if (!hamburgerToggle) return

    hamburgerToggle.classList.toggle(HAMBURGER_TOGGLE_OPENED_CLASS, isOpen)
    hamburgerToggle.setAttribute(ARIA_LABEL, isOpen ? 'Close menu' : 'Open menu')
    hamburgerToggle.dataset.modalTrigger = isOpen ? 'close' : 'hamburger'
  }

  /**
   * Opens a modal with the specified content
   */
  function openModal(contentId) {
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollBarWidth}px`)

    closeAllDropdowns()

    if (headerDropdownContent && headerDropdownContent !== contentId) {
      const previousTrigger = document.querySelector(`[data-modal-trigger="${headerDropdownContent}"]`)
      if (previousTrigger) {
        previousTrigger.setAttribute(ARIA_EXPANDED, 'false')
        previousTrigger.classList.remove(NAVIGATION_ITEM_EXPANDED_CLASS)
      }
    }

    const currentTriggerElement = document.querySelector(`[data-modal-trigger="${contentId}"]`)

    if (currentTriggerElement) {
      currentTriggerElement.setAttribute(ARIA_EXPANDED, 'true')
      if (currentTriggerElement.classList.contains(NAVIGATION_MENU_ITEM_CLASS)) {
        currentTriggerElement.classList.add(NAVIGATION_ITEM_EXPANDED_CLASS)
      }
      currentTriggerButton = currentTriggerElement
    } else {
      currentTriggerButton = null
    }

    toggleHamburgerIcon(true)
    headerDropdownContent = contentId

    const modal = elements.getModal()
    const modalBody = elements.getModalBody()
    const modalContent = elements.getModalContent()

    if (!modal || !modalBody || !modalContent) {
      return
    }

    modal.classList.remove(MODAL_NAVIGATION_CLASS)
    modalBody.innerHTML = ''

    for (const className of Object.values(MODAL_CONTENT_CLASSES)) {
      modalBody.classList.remove(className)
    }

    if (MODAL_CONTENT_CLASSES[contentId]) {
      modalBody.classList.add(MODAL_CONTENT_CLASSES[contentId])
    } else {
      modal.classList.add(MODAL_NAVIGATION_CLASS)
    }

    const contentTemplate = document.querySelector(`#modal-content-${contentId}`)

    if (contentTemplate) {
      applyColumnClassToNavigationItems()
      modalBody.innerHTML = contentTemplate.innerHTML

      // Set aria-label for dialog based on content type
      const dialogLabels = {
        languageSwitcher: 'Country and Language Switcher',
        search: 'Search',
        wishlist: 'Wishlist',
        hamburger: 'Navigation Menu',
      }
      if (dialogLabels[contentId]) {
        modalContent.setAttribute('aria-label', dialogLabels[contentId])
      }

      if (!modal.classList.contains(MODAL_IS_VISIBLE_CLASS)) {
        modal.classList.add(MODAL_IS_VISIBLE_CLASS)
      }

      if (modalKeydownListenerKey === null) {
        modalKeydownListenerKey = eventManager.add(document, 'keydown', handleModalKeydown)
      }

      const reactRoot = modalBody.querySelector('[data-component]')
      if (reactRoot && COMPONENTS_MAP[contentId]) {
        componentManager.mountComponent(contentId, reactRoot)
      }

      if (contentId === 'hamburger') {
        setupMobileNavigation()
      }
    }

    if (!isHeaderOpened) {
      toggleHeaderOpened()
    }

    const closeButton = document.querySelector(`${MODAL_CLOSE_BUTTON_SELECTOR}`)
    const isNavigationModal = modal.classList.contains(MODAL_NAVIGATION_CLASS)

    if (closeButton?.classList.contains(HEADER_MENU_ITEM_IS_HIDDEN_CLASS) && !isNavigationModal) {
      closeButton.classList.remove(HEADER_MENU_ITEM_IS_HIDDEN_CLASS)
    }

    const header = elements.getHeader()
    const headerHeight = header?.offsetHeight || 0
    modal.style.top = null

    if (isTablet() && isNavigationModal) {
      modal.style.top = `${headerHeight}px`
    }
  }

  function updateWishlistCount() {
    const wishlistItems = getWishlist()
    const count = wishlistItems?.length || 0
    const hasItems = count > 0

    const wishlistCount = document.querySelector('.cmp-header-v2__wishlist-count')
    const wishlistButton = document.querySelector('.cmp-header-v2__wishlist-button')
    const wishlistItem = document.querySelector('.cmp-header-v2__wishlist')

    if (wishlistCount) {
      wishlistCount.textContent = hasItems ? count.toString() : ''
    }

    if (wishlistButton) {
      const label = hasItems ? `Wishlist (${count} item${count !== 1 ? 's' : ''})` : 'Wishlist'
      wishlistButton.setAttribute(ARIA_LABEL, label)
    }

    if (wishlistItem) {
      wishlistItem.classList.toggle('cmp-header-v2__menu-item--is-hidden', !hasItems)
    }
  }

  function toggleHeaderOpened() {
    isHeaderOpened = !isHeaderOpened
    const header = elements.getHeader()
    if (!header) return

    header.classList.toggle(HEADER_IS_OPENED_CLASS, isHeaderOpened)
    document.body.classList.toggle('no-scroll', isHeaderOpened)
  }

  function initNavigationItem() {
    const expandedMenuItems = document.querySelectorAll(`.${NAVIGATION_ITEM_EXPANDED_CLASS}`)
    for (const item of expandedMenuItems) {
      item.classList.remove(NAVIGATION_ITEM_EXPANDED_CLASS)
      item.setAttribute(ARIA_EXPANDED, 'false')
    }
  }

  /**
   * Close the modal
   */
  function closeModal() {
    const modal = elements.getModal()
    const modalBody = elements.getModalBody()
    const modalContent = elements.getModalContent()

    if (!modal || !modalBody || !modalContent) return

    if (headerDropdownContent) {
      const triggerElement = document.querySelector(`[data-modal-trigger="${headerDropdownContent}"]`)
      if (triggerElement) {
        triggerElement.setAttribute(ARIA_EXPANDED, 'false')
      }
    }

    initNavigationItem()
    toggleHamburgerIcon(false)

    const closeButton = document.querySelector(`${MODAL_CLOSE_BUTTON_SELECTOR}`)

    if (closeButton && !closeButton.classList.contains(HEADER_MENU_ITEM_IS_HIDDEN_CLASS)) {
      closeButton.classList.add(HEADER_MENU_ITEM_IS_HIDDEN_CLASS)
    }

    completeModalClose()

    function completeModalClose() {
      for (const className of Object.values(modal.classList)) {
        if (className !== MODAL_SELECTOR.replace('#', '')) {
          modal.classList.remove(className)
        }
      }

      if (isHeaderOpened) {
        toggleHeaderOpened()
      }

      for (const className of modalBody.classList) {
        if (className !== MODAL_BODY_CLASS) {
          modalBody.classList.remove(className)
        }
      }

      headerDropdownContent = null

      if (modalKeydownListenerKey !== null) {
        eventManager.remove(modalKeydownListenerKey)
        modalKeydownListenerKey = null
      }

      // Restore focus to the trigger button that opened the modal
      if (currentTriggerButton) {
        currentTriggerButton.focus()
        currentTriggerButton = null
      }
    }
  }

  /**
   * Handle dropdown click
   */
  function handleDropdownClick(contentId, event_, button = null) {
    const isAlreadyOpen = headerDropdownContent === contentId && isHeaderOpened

    if (isAlreadyOpen && (button || event_?.target) && elements.getModal()?.contains(button || event_.target)) {
      return
    }

    isAlreadyOpen ? closeModal() : openModal(contentId)
  }

  /**
   * Register a single set of document-level handlers for all dropdown triggers.
   * Must be called once, after all toggleDropdownClass() calls have populated allDropdownTriggers.
   */
  function initDropdownDocumentHandlers() {
    eventManager.add(document, 'click', (event_) => {
      const clickedInsideTrigger = allDropdownTriggers.some((trigger) => trigger.contains(event_.target))
      if (!clickedInsideTrigger) {
        closeAllDropdowns()
      }
    })

    eventManager.add(document, 'keydown', (event_) => {
      if (event_.key === 'Escape') {
        const openDropdowns = document.querySelectorAll(`.${DROPDOWN_OPENED_CLASS}`)
        if (openDropdowns.length > 0) {
          const firstOpenDropdown = openDropdowns[0]
          const button = firstOpenDropdown.querySelector(DROPDOWN_BUTTON_SELECTOR)
          closeAllDropdowns()
          if (button) {
            button.focus()
          }
        }
      }
    })
  }

  /**
   * Collect dropdown triggers and attach per-trigger click handlers.
   * Document-level handlers are registered separately via initDropdownDocumentHandlers().
   */
  function toggleDropdownClass(triggerSelector) {
    const triggers = [...document.querySelectorAll(triggerSelector)]
    allDropdownTriggers.push(...triggers)

    for (const trigger of triggers) {
      eventManager.add(trigger, 'click', dropdownTriggerClickHandler)
    }
  }

  /**
   * Setup mobile navigation
   */
  function setupMobileNavigation() {
    const modalBody = elements.getModalBody()
    if (!modalBody) {
      return
    }

    const level1Items = modalBody.querySelectorAll('.cmp-navigation-v2__mobile-level1, .cmp-header-v2__division-title')
    const level2Items = modalBody.querySelectorAll('.cmp-navigation-v2__mobile-level2')
    toggleItemsVisibility(level1Items, true)
    toggleItemsVisibility(level2Items, false)
    toggleDropdownVisibility(safeQuerySelector(modalBody, '.cmp-header-v2__division-dropdown'), false)

    for (const level1Item of level1Items) {
      eventManager.add(level1Item, 'click', level1ItemClickHandler)
    }
    attachModalTriggerListeners(modalBody)
  }

  function handleScroll() {
    const headerElement = elements.getHeader()
    if (!headerElement) return

    const currentScrollY = window.scrollY
    const isHeaderOpen = headerElement.classList.contains(HEADER_IS_OPENED_CLASS)
    const isModalHidden = !elements.getModal()?.classList.contains(MODAL_IS_VISIBLE_CLASS) ?? true

    headerElement.classList.toggle(HEADER_IS_SCROLLED_CLASS, currentScrollY > 0 && isModalHidden)
    if (isHeaderOpen) {
      headerElement.classList.remove(HEADER_IS_HIDDEN_CLASS)
      dispatchHeaderHidden(false)
      lastScrollY = currentScrollY
      return
    }

    const isScrollingDown = currentScrollY > lastScrollY

    if (isScrollingDown && currentScrollY > 0) {
      headerElement.classList.add(HEADER_IS_HIDDEN_CLASS)
    } else if (!isScrollingDown) {
      headerElement.classList.remove(HEADER_IS_HIDDEN_CLASS)
    }

    lastScrollY = currentScrollY

    const isHidden = headerElement.classList.contains(HEADER_IS_HIDDEN_CLASS)
    dispatchHeaderHidden(isHidden)
  }

  /**
   * Apply column class to navigation items
   */
  function applyColumnClassToNavigationItems() {
    for (const container of document.querySelectorAll('.cmp-navigation-v2__level2-navigation-items')) {
      const columns = container.querySelectorAll('.cmp-navigation-v2__level2-column')
      const columnCount = columns.length

      const twoColumnWidthCount = container.querySelectorAll(
        '.cmp-navigation-v2__level2-column--two-column-width',
      ).length

      const effectiveColumnCount = columnCount + twoColumnWidthCount
      let colsClass = ''

      if (columnCount === 2 && twoColumnWidthCount === 1) {
        colsClass = 'cmp-navigation-v2__level2-navigation-items--cols-2-mixed'
      } else if (effectiveColumnCount <= 3 && twoColumnWidthCount === 0) {
        colsClass = 'cmp-navigation-v2__level2-navigation-items--cols-3'
      } else if (effectiveColumnCount === 4 || twoColumnWidthCount > 0) {
        colsClass = 'cmp-navigation-v2__level2-navigation-items--cols-4'
      } else if (effectiveColumnCount <= 6) {
        colsClass = 'cmp-navigation-v2__level2-navigation-items--cols-3'
      } else if (effectiveColumnCount <= 8) {
        colsClass = 'cmp-navigation-v2__level2-navigation-items--cols-4'
      }

      if (colsClass) {
        container.classList.add(colsClass)
      }
    }
  }

  /**
   * Cleanup resources
   */
  function cleanup() {
    eventManager.removeAll()
    componentManager.unmountAll()
    window.removeEventListener('wishlistUpdated', updateWishlistCount)

    if (!headerElement.classList.contains(HEADER_IS_FIXED_CLASS)) {
      window.removeEventListener('scroll', debouncedHandleScroll)
    }

    if (elements.getModal().classList.contains(MODAL_IS_VISIBLE_CLASS)) {
      elements.getModal().classList.remove(MODAL_IS_VISIBLE_CLASS)
    }

    initNavigationItem()
  }

  function closeModalOnOutsideClick(event) {
    const modal = elements.getModal()

    if (!modal || !modal?.querySelector(`.${MODAL_OVERLAY_SELECTOR}`)?.contains(event.target)) {
      return
    }

    closeModal()
  }

  /**
   * Dispatch header hidden event
   */
  function dispatchHeaderHidden(isHidden) {
    window.dispatchEvent(new CustomEvent('headerIsHidden', { detail: { isHidden } }))
  }

  /**
   * Initialize the header
   */
  function init() {
    attachModalTriggerListeners()

    const closeButtons = document.querySelectorAll('[data-modal-close]')
    for (const button of closeButtons) {
      eventManager.add(button, 'click', closeModal)
    }

    const hamburgerButton = document.querySelector('.cmp-header-v2__hamburger-toggle')
    if (hamburgerButton) {
      toggleHamburgerIcon(false)
    }

    toggleDropdownClass(COUNTRY_LANGUAGE_SWITCHER_SELECTOR)
    toggleDropdownClass(DIVISION_SELECTOR)
    initDropdownDocumentHandlers()

    updateWishlistCount()
    window.addEventListener('wishlistUpdated', updateWishlistCount)

    if (!headerElement.classList.contains(HEADER_IS_FIXED_CLASS)) {
      window.addEventListener('scroll', debouncedHandleScroll, { passive: true })
    }

    window.addEventListener('beforeunload', cleanup)
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        window.location.reload()
      }
    })

    const modalTriggers = document.querySelectorAll('[data-modal-trigger]')
    for (const trigger of modalTriggers) {
      if (!trigger.hasAttribute(ARIA_EXPANDED)) {
        trigger.setAttribute(ARIA_EXPANDED, 'false')
      }
    }

    window.addEventListener('click', (event) => {
      if (isHeaderOpened) {
        closeModalOnOutsideClick(event)
      }
    })

    const header = elements.getHeader()
    const isHidden = header?.classList.contains(HEADER_IS_HIDDEN_CLASS) ?? false
    dispatchHeaderHidden(isHidden)
  }

  const debouncedHandleScroll = debounce(handleScroll, DEBOUNCE_DEFAULT_WAIT)

  if (document.readyState !== 'loading') {
    init()
  } else {
    document.addEventListener('DOMContentLoaded', init)
  }
})()
